import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';

import packageJSON from '../../../package.json';
import config from '../config/config';
import axios, { AxiosError } from 'axios';
import { RumEvent } from '@datadog/browser-rum-core/cjs/rumEvent.types';

const useECProxy = config.useECService;
//blocked by CSP
const useDataDogLogger = config.datadogConfig.useDataDogLogger;
const useDataDogRUM = config.datadogConfig.useDataDogRUM;

const useConsoleLogger = config.debugMode;

let _loggerHttpService: $TSFixMe;
let loggerParams: { featureFlags?: $TSFixMe } = {};

const loggerHttpService = () => {
    const client = axios.create({
        baseURL: config.eventCollectorUrl,
        headers: {
            'Content-Type': 'application/json'
        }
    });

    return client;
};

const shouldIgnoreEvent = (event: $TSFixMe) => {
    return event?.error?.message?.includes('ResizeObserver loop limit exceeded');
};

const initDataDogBrowser = (useDataDogRUM = true, useDataDogLogger = true) => {
    if (useDataDogRUM) {
        const { connection = { effectiveType: 'not_supported', downlink: 'not_supported' } } = navigator as Navigator & {
            connection?: { downlink: string; effectiveType: string };
        };
        datadogRum.init({
            applicationId: config.datadogConfig.adminRUMApplicationId,
            clientToken: config.datadogConfig.adminClientToken,
            site: config.datadogConfig.datadogSite,
            proxyHost: config.datadogConfig.RUMProxyDomain,
            service: config.datadogConfig.adminServiceName,
            env: config.getENV(),
            version: packageJSON.version,
            sampleRate: 100,
            useCrossSiteSessionCookie: true,
            trackSessionAcrossSubdomains: true,
            trackInteractions: true,
            allowedTracingOrigins: [window.location.origin],
            beforeSend: (event: RumEvent, context: $TSFixMe) => {
                if (shouldIgnoreEvent(event)) {
                    return false;
                }

                if (typeof context?.requestInit?.body === 'string') {
                    try {
                        // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
                        event.context.body = JSON.parse(context.requestInit.body);
                    } catch (error) {
                        // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
                        event.context.body = context.requestInit.body;
                    }
                }

                // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
                event.context.featureFlags = loggerParams.featureFlags;
                // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
                event.context.connection = { type: connection.effectiveType, speed: connection.downlink };
                return true;
            }
        });
    }

    if (useDataDogLogger) {
        datadogLogs.init({
            clientToken: config.datadogConfig.adminClientToken,
            site: config.datadogConfig.datadogSite,
            service: config.datadogConfig.adminServiceName,
            env: config.getENV(),
            version: packageJSON.version,
            forwardErrorsToLogs: true,
            sampleRate: 100,
            useCrossSiteSessionCookie: true,
            trackSessionAcrossSubdomains: true
        });

        datadogLogs.logger.setLevel(config.debugMode ? 'debug' : 'info');

        if (useConsoleLogger) datadogLogs.logger.setHandler('console');
    }
};

const init = () => {
    initDataDogBrowser(useDataDogRUM, useDataDogLogger);

    if (useECProxy) {
        _loggerHttpService = loggerHttpService();
    }
};

type LogStatus = 'debug' | 'info' | 'warn' | 'error';

function isAxiosError(error: $TSFixMe): error is AxiosError {
    return error?.isAxiosError;
}

const error = (message: string, error?: unknown, extraData?: Record<string, unknown> & { errorStatusCode?: number }) => {
    const contextExtraData: Record<string, unknown> & { errorStatusCode?: number; isConsoleSDKError?: boolean } = { ...(extraData || {}) };

    if (isAxiosError(error) && error?.response?.status) {
        Object.assign(contextExtraData, { errorStatusCode: error.response?.status });
    }

    if (error && typeof error === 'object' && 'isConsoleSDKError' in error) {
        Object.assign(contextExtraData, { isConsoleSDKError: true });
    }

    const originalErrorMessage = (error as Error)?.message;

    if (error) {
      //Some errors dont allow to change message property because it has only getters
      try {
        (error as Error).message = message;
      } catch {}
    }

    if (useDataDogRUM) {
        const errorSource = contextExtraData.errorStatusCode ? 'network' : undefined;
        datadogRum.addError(error, { message: originalErrorMessage, ...extraData, ...contextExtraData }, errorSource);
    }

    log(message, { originalErrorMessage, error, ...extraData, ...contextExtraData }, 'error');
};

const log = (message: string, data: $TSFixMe, status: LogStatus = 'debug') => {
    try {
        data = { ...data, userData };

        if (useDataDogLogger) {
            datadogLogs && datadogLogs.logger.log(message, data, status);
        } else {
            if (useECProxy && !useConsoleLogger && status != 'debug')
                _loggerHttpService &&
                    _loggerHttpService.post('event/log', {
                        app: 'communication-center',
                        module: 'admin',
                        level: status,
                        message: message,
                        data,
                        env: config.getENV(),
                        uId: userData.system
                    });

            if (useConsoleLogger) console[status] && console[status](message, data);
        }
    } catch (error) {
        console.error('error in logging system', error);
    }
};

let userData: { system?: $TSFixMe; id?: $TSFixMe; environment?: $TSFixMe; impersonatorId?: $TSFixMe } = {};

const setUser = ({ systemId, id, environmentId, impersonatorId }: $TSFixMe) => {
    userData = { id, system: systemId, environment: environmentId, impersonatorId };

    datadogRum && datadogRum.setUser({ ...userData });
};

const removeUser = () => {
    userData = {};

    datadogRum && datadogRum.removeUser();
};

const logger = { init, setUser, removeUser, log, error };

export const setLoggerParams = (_loggerParams: typeof loggerParams) => (loggerParams = _loggerParams);

export default logger;
